import React from 'react';
import { useTheme } from '../context/ThemeContext';

/**
 * Shared skeleton-loading primitives — replaces LoadingSpinner (the old
 * radar-HUD spinner) app-wide. A skeleton that roughly matches the shape of
 * the content about to arrive reads as "this is loading" without the jarring
 * swap a spinner-then-content transition causes, and it's what the rest of
 * the industry has converged on for exactly this reason.
 *
 * `Skeleton` itself was lifted out of TrendingToday.js (the first page to
 * use this pattern) so every page shares one implementation instead of each
 * hand-rolling its own pulsing div.
 */

// Base pulsing block. Sizing is Tailwind h-/w- utility classes (so callers
// can compose freely); background + radius are inline since the former
// needs the current theme's token and the latter must survive whatever
// utility classes `extra` adds.
export function Skeleton({ h = 'h-4', w = 'w-full', extra = '', square = false }) {
  const { colors: C } = useTheme();
  return <div className={`${h} ${w} ${extra} animate-pulse`} style={{ background: C.surface, borderRadius: square ? 0 : '4px' }} />;
}

// A few text lines of varying width — stands in for a paragraph, label
// group, or any block of short copy.
export function SkeletonLines({ count = 3, lastWidth = 'w-2/3' }) {
  return (
    <div className="space-y-2">
      {[...Array(count)].map((_, i) => (
        <Skeleton key={i} h="h-3.5" w={i === count - 1 ? lastWidth : 'w-full'} />
      ))}
    </div>
  );
}

// A row of stat-tile-shaped blocks (the app's StatTile is a bordered box of
// roughly this footprint) — for headline-number rows above a page's main
// content.
export function SkeletonStatRow({ count = 3 }) {
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}>
      {[...Array(count)].map((_, i) => <Skeleton key={i} h="h-14" />)}
    </div>
  );
}

// A bordered card shell — a header line plus a few content lines — for
// card-grid layouts (comparisons, sector tiles, saved-research entries).
export function SkeletonCard({ lines = 3 }) {
  const { colors: C } = useTheme();
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: '6px', padding: '0.9rem', background: C.bg }}>
      <Skeleton h="h-4" w="w-1/2" extra="mb-3" />
      <SkeletonLines count={lines} />
    </div>
  );
}

export function SkeletonCardGrid({ cards = 6, lines = 3, cols = 'sm:grid-cols-2 lg:grid-cols-3' }) {
  return (
    <div className={`grid grid-cols-1 ${cols} gap-3`}>
      {[...Array(cards)].map((_, i) => <SkeletonCard key={i} lines={lines} />)}
    </div>
  );
}

// A bordered table shell — header bar + N pulsing rows — for any list/table
// view (screeners, rankings, flow tables).
export function SkeletonTable({ rows = 6 }) {
  const { colors: C } = useTheme();
  return (
    <div style={{ borderRadius: '6px', overflow: 'hidden', border: `1px solid ${C.border}` }}>
      <Skeleton h="h-10" square />
      <div className="p-2" style={{ background: C.bg }}>
        {[...Array(rows)].map((_, i) => (
          <div key={i} className="py-2.5 px-2" style={{ borderBottom: i < rows - 1 ? `1px solid ${C.border}` : 'none' }}>
            <Skeleton h="h-4" w={i % 2 ? 'w-2/3' : 'w-4/5'} />
          </div>
        ))}
      </div>
    </div>
  );
}

// Generic full-page skeleton — title bar + an optional stat row + N
// table-shaped sections. The reasonable default for a page whose loading
// state isn't table/card-specific enough to warrant a bespoke layout.
export function SkeletonPage({ sections = 3, statTiles = 0 }) {
  return (
    <div className="space-y-6">
      <div className="flex justify-between"><Skeleton h="h-8" w="w-52" /><Skeleton h="h-9" w="w-28" /></div>
      {statTiles > 0 && <SkeletonStatRow count={statTiles} />}
      {[...Array(sections)].map((_, i) => <SkeletonTable key={i} rows={4} />)}
    </div>
  );
}

// A handful of bordered list-row shells — for a flat vertical list of items
// (saved research, alerts) rather than a full table with a header bar.
export function SkeletonRows({ count = 4 }) {
  const { colors: C } = useTheme();
  return (
    <div className="flex flex-col gap-2">
      {[...Array(count)].map((_, i) => (
        <div key={i} style={{ padding: '0.6rem 0.85rem', border: `1px solid ${C.border}`, borderRadius: '0.4rem' }}>
          <Skeleton h="h-4" w={i % 2 ? 'w-1/3' : 'w-1/2'} />
        </div>
      ))}
    </div>
  );
}

// A small inline skeleton bar — for an in-place "this one section/button's
// result is loading" state that shouldn't take over the whole page (e.g. an
// AI answer streaming in below a form that stays interactive).
export function SkeletonInline({ lines = 2 }) {
  return (
    <div style={{ padding: '0.6rem 0' }}>
      <SkeletonLines count={lines} />
    </div>
  );
}
